import {
  AffectedProductStatus,
  RowState,
  SpotlightProduct,
  SpotlightStatus,
} from './spotlightDetails.types';
import { usePersona } from '../../../components/hooks/usePersona';
import { Group, Text, Button } from '@cowbell/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faHeadset,
  faClose,
  faCircleXmark,
} from '@fortawesome/pro-regular-svg-icons';
import { SpotlightUserAccessMode } from '../getSpotlightUserAccessMode';

interface ActionColumnProps {
  persona: ReturnType<typeof usePersona>;
  status: AffectedProductStatus;
  product: SpotlightProduct;
  onUndo: (productName: string) => void;
  onPatch: (productName: string, customerVersion: string) => void;
  rowState: RowState;
  onSubmitForReview: (productName: string, customerVersion: string) => void;
  onNotApplicable: (productName: string) => void;
  handleContactCyberExpert: (productName: string) => void;
  onRequestDecline: (productName: string, customerVersion: string) => void;
  spotlightStatus: SpotlightStatus;
  userAccessMode: SpotlightUserAccessMode;
}

const ActionColumn = (props: ActionColumnProps) => {
  const {
    persona,
    status,
    product,
    onUndo,
    onPatch,
    rowState,
    onSubmitForReview,
    onNotApplicable,
    handleContactCyberExpert,
    onRequestDecline,
    spotlightStatus,
    userAccessMode,
  } = props;

  if (status === AffectedProductStatus.PATCHED) {
    return (
      <Group gap="xs">
        <FontAwesomeIcon
          icon={faCheck}
          color="var(--custom-text-success-default)"
        />
        <Text size="md" c="var(--custom-text-success-default)" fw={500}>
          Patched
        </Text>
      </Group>
    );
  }

  if (status === AffectedProductStatus.NOT_APPLICABLE) {
    return (
      <Group gap="xs">
        <FontAwesomeIcon
          icon={faCircleXmark}
          color="var(--mantine-color-gray-6)"
        />
        <Text size="md" c="var(--mantine-color-gray-6)" fw={500}>
          Not Applicable
        </Text>
        {userAccessMode === 'readwrite' && (
          <>
            {spotlightStatus !== SpotlightStatus.PATCHED && (
              <Button
                variant="default"
                onClick={() => onUndo(product.name)}
                fw={500}
              >
                Undo
              </Button>
            )}
            {persona.isInsured && (
              <Button
                variant="subtle"
                size="sm"
                leftSection={<FontAwesomeIcon icon={faHeadset} />}
                onClick={() => handleContactCyberExpert(product.name)}
                fw={500}
              >
                Contact Cyber Expert
              </Button>
            )}
          </>
        )}
      </Group>
    );
  }

  if (persona.isInsured) {
    if (status === AffectedProductStatus.UNPATCHED) {
      if (userAccessMode === 'readonly') return null;

      return (
        <Group gap="xs">
          <Button
            size="sm"
            fw={500}
            onClick={() =>
              onSubmitForReview(product.name, rowState.customerVersion)
            }
          >
            Submit for Review
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={() => onNotApplicable(product.name)}
            fw={500}
          >
            Not Applicable
          </Button>
          <Button
            variant="subtle"
            size="sm"
            leftSection={<FontAwesomeIcon icon={faHeadset} />}
            onClick={() => handleContactCyberExpert(product.name)}
            fw={500}
          >
            Contact Cyber Expert
          </Button>
        </Group>
      );
    }

    if (status === AffectedProductStatus.SUBMIT_FOR_REVIEW) {
      return (
        <Group gap="xs">
          <FontAwesomeIcon
            icon={faCheck}
            color="var(--custom-text-success-default)"
          />
          <Text size="md" fw={500} c="var(--custom-text-success-default)">
            Submitted for Review
          </Text>
        </Group>
      );
    }

    if (status === AffectedProductStatus.DECLINED) {
      return (
        <Group gap="xs">
          <FontAwesomeIcon icon={faClose} color="var(--mantine-color-red-6)" />
          <Text size="md" fw={500} c="var(--mantine-color-red-6)">
            Declined
          </Text>
          {userAccessMode === 'readwrite' && (
            <Button
              size="sm"
              fw={500}
              onClick={() =>
                onSubmitForReview(product.name, rowState.customerVersion)
              }
            >
              Re-submit for Review
            </Button>
          )}
        </Group>
      );
    }

    return null;
  }

  if (status === AffectedProductStatus.DECLINED) {
    return (
      <Group gap="xs">
        <FontAwesomeIcon icon={faClose} color="var(--mantine-color-red-6)" />
        <Text size="md" fw={500} c="var(--mantine-color-red-6)">
          Declined
        </Text>
      </Group>
    );
  }

  return (
    <Group gap="xs">
      {status === AffectedProductStatus.SUBMIT_FOR_REVIEW && (
        <Group gap="xs">
          <FontAwesomeIcon
            icon={faCheck}
            color="var(--custom-text-success-default)"
          />
          <Text size="md" fw={500} c="var(--custom-text-success-default)">
            Submitted for Review
          </Text>
        </Group>
      )}
      {userAccessMode === 'readwrite' && (
        <>
          <Button
            color="green"
            size="sm"
            onClick={() => onPatch(product.name, rowState.customerVersion)}
            fw={500}
          >
            Patch
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={() => onNotApplicable(product.name)}
            fw={500}
          >
            Not Applicable
          </Button>
          {status === AffectedProductStatus.SUBMIT_FOR_REVIEW &&
            rowState.customerVersion && (
              <Button
                variant="outline"
                color="red"
                size="sm"
                fw={500}
                onClick={() =>
                  onRequestDecline(product.name, rowState.customerVersion)
                }
              >
                Decline
              </Button>
            )}
        </>
      )}
    </Group>
  );
};

export default ActionColumn;
